import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, GripVertical, Lock } from "lucide-react";

/**
 * Concierge (Mordomo Places) Editor — CMS for the 5-step interactive lead form.
 *
 * Rules enforced here:
 *  - The last step is ALWAYS "Seu WhatsApp" (locked; user cannot remove/edit its key).
 *  - Prior steps can be added/reordered/edited. Each step has either options (chips)
 *    OR a free-text input. Both title (question) and its options are per-language (pt/en/es).
 */

let _sid = 0;
const newKey = () => `q_${Date.now()}_${++_sid}`;

// The final WhatsApp step is required and locked.
const WHATSAPP_STEP = {
  key: "whatsapp",
  locked: true,
  input: true,
  q: { pt: "Seu WhatsApp", en: "Your WhatsApp", es: "Tu WhatsApp" },
  placeholder: { pt: "Ex.: (62) 9 9999-9999", en: "Your phone number", es: "Tu número de WhatsApp" },
};

const withKey = (o) => ({ ...o, _k: o._k ?? newKey() });

export default function ConciergeEditor() {
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("pt");

  useEffect(() => {
    api.get("/content/kv/concierge_questions").then(({ data }) => {
      const arr = Array.isArray(data) ? data.map(withKey) : [];
      // Ensure the last step is always WhatsApp
      const withoutWa = arr.filter((s) => s.key !== "whatsapp");
      setSteps([...withoutWa, withKey({ ...WHATSAPP_STEP })]);
    }).catch(() => {
      setSteps([withKey({ ...WHATSAPP_STEP })]);
    });
  }, []);

  const upd = (i, k, v) => setSteps((arr) => { const a = [...arr]; a[i] = { ...a[i], [k]: v }; return a; });
  const updLangField = (i, field, l, v) => setSteps((arr) => {
    const a = [...arr]; const cur = a[i][field] || {}; a[i] = { ...a[i], [field]: { ...cur, [l]: v } }; return a;
  });
  const add = () => setSteps((arr) => {
    // Insert before the WhatsApp step
    const wa = arr[arr.length - 1];
    const before = arr.slice(0, -1);
    return [...before, withKey({ key: `q_${before.length + 1}`, q: { pt: "", en: "", es: "" }, options: [] }), wa];
  });
  const remove = (i) => setSteps((arr) => {
    if (arr[i]?.locked) return arr;
    return arr.filter((_, j) => j !== i);
  });
  const move = (i, dir) => setSteps((arr) => {
    if (arr[i]?.locked || arr[i + dir]?.locked) return arr;
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; return a;
  });
  const setOptions = (i, csv) => setSteps((arr) => {
    const a = [...arr]; a[i] = { ...a[i], options: csv.split(",").map((s) => s.trim()).filter(Boolean) }; return a;
  });
  const toggleInput = (i) => setSteps((arr) => {
    const a = [...arr]; const next = !a[i].input; a[i] = { ...a[i], input: next, options: next ? [] : a[i].options }; return a;
  });

  const save = async () => {
    setSaving(true);
    try {
      // Strip _k before persisting; guarantee last is WhatsApp
      const clean = steps.map(({ _k, ...rest }) => rest);
      const withoutWa = clean.filter((s) => s.key !== "whatsapp");
      const final = [...withoutWa, { ...WHATSAPP_STEP }];
      await api.put("/content/kv/concierge_questions", { value: final });
      toast.success("Perguntas do Mordomo salvas.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl">Mordomo Places — Perguntas</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-xl">
            Configure as perguntas e opções (botões) do Mordomo. A última pergunta é sempre <b>“Seu WhatsApp”</b> e não pode ser removida.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex border border-black/15 rounded-full overflow-hidden text-xs">
            {["pt", "en", "es"].map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 ${lang === l ? "bg-black text-white" : "hover:bg-black/5"} transition-colors`} data-testid={`concierge-lang-${l}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <button onClick={add} data-testid="concierge-q-add" className="text-xs bg-accent-yellow px-3 py-2 rounded-full font-semibold hover:bg-black hover:text-white transition-colors"><Plus className="w-3 h-3 inline" /> Nova pergunta</button>
          <button onClick={save} disabled={saving} data-testid="concierge-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange transition-colors disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>

      <div className="space-y-4">
        {steps.map((s, i) => (
          <section key={s._k} className={`border rounded-md p-5 ${s.locked ? "border-accent-orange/40 bg-orange-50/30" : "border-black/10"}`} data-testid={`concierge-step-${i}`}>
            <div className="flex items-center gap-3 mb-4">
              {!s.locked ? (
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} className="text-neutral-400 hover:text-black transition-colors" title="Subir"><GripVertical className="w-3 h-3" /></button>
                </div>
              ) : (
                <Lock className="w-4 h-4 text-accent-orange" />
              )}
              <span className="text-xs font-mono text-neutral-400">Passo {i + 1}</span>
              {s.locked && <span className="text-[10px] uppercase tracking-widest bg-accent-orange text-white px-2 py-0.5 rounded">Última — obrigatória</span>}
              <input value={s.key || ""} onChange={(e) => !s.locked && upd(i, "key", e.target.value.replace(/\s+/g, "_"))} readOnly={s.locked} placeholder="chave-interna (ex: profile)" className={`text-xs font-mono border border-black/15 px-2 py-1 rounded ${s.locked ? "bg-black/5" : ""}`} data-testid={`concierge-key-${i}`} />
              <label className="text-xs text-neutral-500 flex items-center gap-1 ml-auto">
                <input type="checkbox" checked={!!s.input} onChange={() => !s.locked && toggleInput(i)} disabled={s.locked} data-testid={`concierge-input-toggle-${i}`} />
                Campo de texto livre (sem botões)
              </label>
              {!s.locked && (
                <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700 transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-neutral-500">Pergunta ({lang.toUpperCase()})</label>
                <input value={(s.q && s.q[lang]) || ""} onChange={(e) => updLangField(i, "q", lang, e.target.value)} data-testid={`concierge-q-${i}-${lang}`} className="w-full border border-black/15 px-3 py-2 rounded text-sm" placeholder="Ex.: Qual o maior desafio agora?" />
              </div>
              {s.input ? (
                <div>
                  <label className="text-xs text-neutral-500">Placeholder do campo ({lang.toUpperCase()})</label>
                  <input value={(s.placeholder && s.placeholder[lang]) || ""} onChange={(e) => updLangField(i, "placeholder", lang, e.target.value)} data-testid={`concierge-placeholder-${i}-${lang}`} className="w-full border border-black/15 px-3 py-2 rounded text-sm" placeholder="Ex.: Ex.: Goiânia/GO" />
                </div>
              ) : (
                <div>
                  <label className="text-xs text-neutral-500">Opções (botões) — separadas por vírgula</label>
                  <input value={(s.options || []).join(", ")} onChange={(e) => setOptions(i, e.target.value)} data-testid={`concierge-options-${i}`} className="w-full border border-black/15 px-3 py-2 rounded text-sm" placeholder="Incorporadora, Loteadora, Construtora" />
                  {(s.options || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(s.options || []).map((o) => <span key={o} className="text-[11px] border border-black/10 bg-white px-2 py-0.5 rounded-full">{o}</span>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
